// Shared string utilities (browser-safe version)
export class StringUtils {
    static readonly validationPatterns = {
        latin: /^[a-zA-Z]*$/,
        numeric: /^[0-9]*$/,
        alphabetic: /^[a-zA-ZÀ-ÿ]*$/,
        latinalphabetic: /^[a-zA-Z]*$/,
        alphanumeric: /^[a-z0-9]+$/i,
        ascii: /^[\u0000-\u007F]*$/,
        hexadecimal: /^[0-9a-fA-F]*$/,
        mrz: /^[A-Z0-9<]{44}$/i,
        specialChars: /^[^a-z0-9]+$/i,
        email: /^\S+@\S+\.\S+$/,
        dateISO: /^\d{4}-\d{2}-\d{2}$/,
        postalCode: /^\d{5}-\d{3}$/,
        time24h: /^([01]\d|2[0-3]):([0-5]\d)$/,
        url: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
        uuid: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        ipv6: /^([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4})$/,
        ipv4: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        letter: /^[a-zA-Z]$/,
        scripts: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    } as const;

    static isAlphabetic(value: string): boolean { return this.validationPatterns.alphabetic.test(value); }
    static isAlphanumeric(value: string): boolean { return this.validationPatterns.alphanumeric.test(value); }
    static isAscii(value: string): boolean { return this.validationPatterns.ascii.test(value); }
    static isHexadecimal(value: string): boolean { return this.validationPatterns.hexadecimal.test(value); }
    static isLatin(value: string): boolean { return this.validationPatterns.latin.test(value); }
    static isMrz(value: string): boolean { return this.validationPatterns.mrz.test(value); }
    static isNumeric(value: string): boolean { return this.validationPatterns.numeric.test(value); }
    static isEmail(email: string): boolean { return this.validationPatterns.email.test(email); }
    static isUrl(url: string): boolean { return this.validationPatterns.url.test(url); }
    static isPostalCode(postalCode: string): boolean { return this.validationPatterns.postalCode.test(postalCode); }
    static isValidDate(date: string): boolean { return !isNaN(Date.parse(date)); }
    static isValidTime24h(time: string): boolean { return this.validationPatterns.time24h.test(time); }
    static isPalindrome(str: string): boolean {
        const cleaned = str.toLowerCase().replace(/[^a-z0-9]/g, '');
        return cleaned === [...cleaned].reverse().join('');
    }
    static isEmpty(value: string): boolean { return value.trim().length === 0; }
    static hasMinimumLength(value: string, length: number): boolean { return value.length >= length; }
    static hasMaximumLength(value: string, length: number): boolean { return value.length <= length; }
    static hasNumbers(value: string): boolean { return /\d/.test(value); }
    static hasSpecialCharacters(value: string): boolean { return /[!@#$%^&*(),.?":{}|<>]/.test(value); }
    static hasUppercase(value: string): boolean { return /[A-Z]/.test(value); }
    static hasLowercase(value: string): boolean { return /[a-z]/.test(value); }
    static hasSpaces(value: string): boolean { return /\s/.test(value); }
    static hasScripts(value: string): boolean { return this.validationPatterns.scripts.test(value); }
    static hasSpecialChars(value: string): boolean { return this.validationPatterns.specialChars.test(value); }
    static isLetter(value: string): boolean { return this.validationPatterns.letter.test(value); }

    static countOccurrences(str: string, search: string): number { return str.split(search).length - 1; }

    static capitalize(str: string): string { return str.charAt(0).toUpperCase() + str.slice(1); }
    static toKebabCase(str: string): string { return str.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase(); }
    static toSnakeCase(str: string): string { return str.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/\s+/g, '_').toLowerCase(); }
    static toCamelCase(str: string): string { return str.replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : '')).replace(/^(.)/, char => char.toLowerCase()); }
    static reverseString(str: string): string { return str.split('').reverse().join(''); }
    static truncate(str: string, maxLength = 100, ellipsis = '...'): string { return str.length <= maxLength ? str : str.slice(0, maxLength) + ellipsis; }
    static truncateMiddle(value: string, startLength = 18, endLength = 6, maxLength = 30, ellipsis = '…'): string {
        if (typeof value !== 'string') return '';
        const trimmed = value.trim();
        if (!trimmed || trimmed.length <= maxLength) return trimmed;
        return `${trimmed.slice(0, startLength)}${ellipsis}${trimmed.slice(-endLength)}`;
    }
    static trimExtraSpaces(str: string): string { return str.replace(/\s+/g, ' ').trim(); }
    static normalize(value: string): string { return value.trim().toLowerCase(); }

    /** Removes diacritics/accents from a string (e.g. "ção" → "cao"). */
    static stripDiacritics(value: string): string { return value.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }

    /** Converts a string to a slug: removes diacritics, lowercases, replaces non-alphanumeric runs with `_`, and trims leading/trailing underscores. */
    static slugify(value: string): string {
        return StringUtils.stripDiacritics(value).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    }
    static getCleanFileName(value: string): string {
        const lastSlash = Math.max(value.lastIndexOf('/'), value.lastIndexOf('\\'));
        const fileName = lastSlash >= 0 ? value.slice(lastSlash + 1) : value;
        const lastDot = fileName.lastIndexOf('.');
        return lastDot >= 0 ? fileName.slice(0, lastDot) : fileName;
    }

    // Returns undefined for non-strings or empty-after-trim
    static toOptionalTrimmed(value?: string | null): string | undefined {
        if (typeof value !== 'string') return undefined;
        const trimmed = value.trim();
        return trimmed.length === 0 ? undefined : trimmed;
    }

    // Returns string array with trimmed, non-empty values; empty array if none
    static toOptionalStringArray(values?: Array<string | null | undefined> | null): string[] {
        if (!Array.isArray(values)) return [];
        return values
            .map((value) => (typeof value === 'string' ? value.trim() : ''))
            .filter((value) => value.length > 0);
    }

    // Returns trimmed string or empty when input is not a string
    static toRequiredTrimmed(value?: string | null): string {
        if (typeof value !== 'string') return '';
        return value.trim();
    }

    static toBrazilianPhoneString(value?: string | null, fallback = '—'): string {
        if (!value) return fallback;
        const digits = value.replace(/\D/g, '');
        if (digits.length === 11) {
            return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        if (digits.length === 10) {
            return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return value;
    }

    static isCodeLike(value?: string | null): boolean {
        if (!value) return false;
        const trimmed = value.trim();
        if (trimmed.length === 0) return false;
        const normalized = trimmed.replace(/\s+/g, '');
        if (normalized.length === 0) return false;
        const LOWER_CODE_REGEX = /^[a-z0-9_]+$/;
        const UPPER_CODE_REGEX = /^[A-Z0-9_]+$/;
        if (normalized.includes('_') || normalized.includes('-')) {
            return LOWER_CODE_REGEX.test(normalized) || UPPER_CODE_REGEX.test(normalized);
        }
        return LOWER_CODE_REGEX.test(normalized) && normalized === normalized.toLowerCase();
    }

    static toComparisonKey(value: string): string {
        return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
    }

    static getInitials(value?: string | null, fallback = 'U'): string {
        if (!value) return fallback;
        const parts = value
            .trim()
            .split(/\s+/)
            .filter(Boolean);
        if (!parts.length) return fallback;
        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
}
